import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input } from "reactstrap";
import { getTableDashboard, fetchBranches } from "../../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";

const statusBadge = (s) => {
  const map = {
    pending: { color: C.orange, bg: C.orangeLight },
    confirmed: { color: C.blue, bg: C.blueLight },
    seated: { color: C.purple, bg: C.purpleLight },
    completed: { color: C.green, bg: C.greenLight },
    cancelled: { color: C.textLight, bg: C.borderLight },
    no_show: { color: C.coral, bg: C.coralLight },
  };
  return map[s] || { color: C.textMed, bg: C.borderLight };
};

const TableDashboard = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      setBranches(list);
    };
    load();
  }, [selectedMerchant]);

  const fetchDashboard = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") { setData(null); return; }
    setIsLoading(true);
    const params = { MerchantId: selectedMerchant };
    if (selectedBranch) params.branchId = selectedBranch;
    const result = await getTableDashboard(params);
    setData(result);
    setIsLoading(false);
  }, [selectedMerchant, selectedBranch]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard, selectedMerchant]);

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Dashboard" parent="Table Management" />
      <Container fluid className="rpt-wrap">
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          <Input type="select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} style={{ maxWidth: 250, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
          <div style={{ flex: 1 }} />
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={() => navigate("/table-management/tables")}>View Tables</Button>
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={() => navigate("/reservations/list")}>Reservations</Button>
          <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={() => navigate("/table-management/waitlist")}>Waitlist</Button>
        </div>

        {isLoading && !data ? <Loader /> : (!selectedMerchant || selectedMerchant === "all") ? (
          <RCard><p style={{ textAlign: "center", color: C.textLight }}>Select a merchant</p></RCard>
        ) : !data ? (
          <RCard><p style={{ textAlign: "center", color: C.textLight }}>No data</p></RCard>
        ) : (
          <>
            {/* Tables */}
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Tables</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
              <Tile label="Available" value={data.tables.available} gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} icon="✓" delay={0} />
              <Tile label="Occupied" value={data.tables.occupied} gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} icon="●" delay={80} />
              <Tile label="Reserved" value={data.tables.reserved} gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} icon="◆" delay={160} />
              <Tile label="Occupancy" value={`${data.tables.occupancyRate}%`} gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} icon="📊" delay={240} />
            </div>

            {/* Reservations */}
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Today's Reservations</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16, marginBottom: 24 }}>
              <Tile label="Total" value={data.reservations.total} gradient={`linear-gradient(135deg, #64748B, #94A3B8)`} icon="📋" delay={0} sub={<span style={{ cursor: "pointer", color: C.blue }} onClick={() => navigate("/reservations/list")}>View all →</span>} />
              <Tile label="Pending" value={data.reservations.pending} gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} icon="⏳" delay={80} />
              <Tile label="Confirmed" value={data.reservations.confirmed} gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} icon="✓" delay={160} />
              <Tile label="Seated" value={data.reservations.seated} gradient={`linear-gradient(135deg, ${C.purple}, #C084FC)`} icon="🪑" delay={240} />
              <Tile label="Completed" value={data.reservations.completed} gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} icon="✓" delay={320} />
              <Tile label="No-Show" value={data.reservations.no_show} gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} icon="✕" delay={400} />
            </div>

            {/* Waitlist */}
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Active Waitlist</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
              <Tile label="Waiting" value={data.waitlist.waiting} gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} icon="⏳" delay={0} sub={<span style={{ cursor: "pointer", color: C.blue }} onClick={() => navigate("/table-management/waitlist")}>View all →</span>} />
              <Tile label="Notified" value={data.waitlist.notified} gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} icon="📢" delay={80} />
              <Tile label="Total Active" value={data.waitlist.total} gradient={`linear-gradient(135deg, #64748B, #94A3B8)`} icon="📋" delay={160} />
              <Tile label="Avg Wait" value={`${data.waitlist.avgWaitMinutes}m`} gradient={`linear-gradient(135deg, ${C.purple}, #C084FC)`} icon="⏱" delay={240} />
            </div>

            {/* Upcoming Reservations Table */}
            {data.upcomingReservations?.length > 0 && (
              <RCard title="Upcoming (Next 2 Hours)" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        {["Time", "Customer", "Guests", "Table", "Branch", "Status"].map((h) => <th key={h} style={tbl.th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {data.upcomingReservations.map((r) => {
                        const sb = statusBadge(r.status);
                        return (
                          <tr key={r.id} className="rpt-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/reservations/edit/${r.id}`)}>
                            <td style={{ ...tbl.td, fontWeight: 700 }}>{r.startTime}</td>
                            <td style={tbl.td}>{r.customerName}</td>
                            <td style={{ ...tbl.td, fontWeight: 700 }}>{r.guestCount}</td>
                            <td style={tbl.td}>{r.Table ? <Badge color={C.blue} bg={C.blueLight}>{r.Table.name}</Badge> : <span style={{ color: C.coral, fontWeight: 600 }}>Unassigned</span>}</td>
                            <td style={tbl.td}>{r.Branch?.name || "—"}</td>
                            <td style={tbl.td}><Badge color={sb.color} bg={sb.bg}>{r.status}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </RCard>
            )}
          </>
        )}
      </Container>
    </Fragment>
  );
};

export default TableDashboard;
